import { DOCUMENT, formatDate } from '@angular/common';
import {
	AfterViewInit, Component, ElementRef, EmbeddedViewRef, Inject, OnDestroy, OnInit,
	QueryList, TemplateRef, ViewChild, ViewChildren, ViewContainerRef
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { DatePickerPresets, GlobalFunctions } from '@global/GlobalFunctions';
import { Account, AccountFeatures, AccountsService, IAccount } from '@services/accounts.service';
import { AssetsDataService, LevelAsset } from '@services/assets-data.service';
import { AuthenticationService, IWebAppUser } from '@services/authentication.service';
import { Organisation, OrganisationDashboard, OrganisationDataModelNode, OrganisationDataService, OrganisationsHierarchy } from '@services/organisation-data.service';
import { combineLatest, distinctUntilChanged, filter, finalize, firstValueFrom, interval, Subject, take, takeUntil, tap } from 'rxjs';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { DashboardLayoutComponent, PanelModel } from '@syncfusion/ej2-angular-layouts';
import { ChartComponent, FontModel, SeriesModel } from '@syncfusion/ej2-angular-charts';
import { CircularGaugeComponent } from '@syncfusion/ej2-angular-circulargauge';
import { AssetDataPoint, DataPoint, ISensorData } from '@services/datapoints-data.service';
import { convertEpochToDateString, isExpired } from '@functions/functions';
import { ListViewComponent } from '@syncfusion/ej2-angular-lists';

export class PanelData {
	id: string;
	numMetrics: number;
	metricLabels: string[];
	numberMetrics: number[];
	dataMetrics: ISensorData[][];
	SI: string;
	max?: number;
	min?: number;
	interval?: number;
	series?: SeriesModel[];
	majorTicks?: Object;
	minorTicks?: Object;
	metricColours?: string[];
	barSeries?: SeriesModel;
	activeSeries?: SeriesModel[];
	datapoints?: DataPoint[];
	datapointMetrics?: { datapoint: DataPoint, metrics: number[] }[];
}

@Component({
	selector: 'app-custom-dashboard',
	templateUrl: './custom-dashboard.component.html',
	styleUrls: ['./custom-dashboard.component.scss']
})
export class CustomDashboardComponent implements AfterViewInit, OnInit, OnDestroy {

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/** Selected Organisation ID */
	public organisationUID: string;

	/** Selected Organisation */
	public organisation: Organisation;

	/** accountID */
	public accountGUID: string;

	/** selectedAccount */
	public selectedAccount: IAccount;

	/** this user */
	public thisUser: IWebAppUser;

	/** cellSpacing */
	public cellSpacing: number[] = [10, 10];

	/** aspectRatio */
	public aspectRatio: any = 100 / 68;

	/** aspectRatio */
	public mobileAspectRatio: any = 100 / 68;

	/** maxRows */
	public maxRows: number = 10;

	/** maxColumns */
	public maxColumns: number = 10;

	/** existingDashboards */
	public existingDashboards: OrganisationDashboard[] = [];

	/** selectedDashboard */
	public selectedDashboard: OrganisationDashboard | undefined;

	/** dashboard */
	@ViewChild('dashboardLayout') dashboard!: DashboardLayoutComponent;

	/** panelInfo */
	public panelInfo: {
		id: string,
		template: string,
		templateID: number,
		metrics: number[],
		context: any,
		complete?: boolean,
		isHierarchy?: boolean,
		barDefault?: boolean
	}[] = [];

	/** dashboardName */
	public dashboardName: string = "";

	/** templates */
	public templates: TemplateRef<any>[] = [];

	/** hierarchyTemplates */
	public hierarchyTemplates: TemplateRef<any>[] = [];

	/** textPanelTemplate */
	@ViewChild('textPanelTemplate') textPanelTemplate: TemplateRef<any>;

	/** gaugePanelTemplate */
	@ViewChild('gaugePanelTemplate') gaugePanelTemplate: TemplateRef<any>;

	/** chartPanelTemplate */
	@ViewChild('chartPanelTemplate') chartPanelTemplate: TemplateRef<any>;

	/** listPanelTemplate */
	@ViewChild('listPanelTemplate') listPanelTemplate: TemplateRef<any>;

	/** shortcutPanelTemplate */
	@ViewChild('shortcutPanelTemplate') shortcutPanelTemplate: TemplateRef<any>;

	/** watchListPanelTemplate */
	@ViewChild('watchListPanelTemplate') watchListPanelTemplate: TemplateRef<any>;

	/** actionsPanelTemplate */
	@ViewChild('actionsPanelTemplate') actionsPanelTemplate: TemplateRef<any>;

	/** rulesPanelTemplate */
	@ViewChild('rulesPanelTemplate') rulesPanelTemplate: TemplateRef<any>;

	/** eventsPanelTemplate */
	@ViewChild('eventsPanelTemplate') eventsPanelTemplate: TemplateRef<any>;

	/** webhooksPanelTemplate */
	@ViewChild('webhooksPanelTemplate') webhooksPanelTemplate: TemplateRef<any>;

	/** linearGaugePanelTemplate */
	@ViewChild('linearGaugePanelTemplate') linearGaugePanelTemplate: TemplateRef<any>;

	/** annotatedGaugePanelTemplate */
	@ViewChild('annotatedGaugePanelTemplate') annotatedGaugePanelTemplate: TemplateRef<any>;

	/** allGauges */
	@ViewChildren('gauge') allGauges!: QueryList<CircularGaugeComponent>;

	/** allCharts */
	@ViewChildren('chart') allCharts!: QueryList<ChartComponent>;

	/** allLists */
	@ViewChildren('list') allLists!: QueryList<ListViewComponent>;

	/** Chart Legend */
	public legend: Object;

	/** Chart Zoom Settings */
	public zoomSettings: Object;

	/** crosshair */
	public crosshair: object = {
		enable: true,
		lineType: 'Vertical'
	};

	/** Sensor pen Pallet Colours */
	public palette: object;

	/** Tooltip Settings */
	public tooltipSettings: Object;

	/** Chart boarder Settings */
	public borderSettings: Object;

	/**
	 * Chart Selection mode
	 * None|Point|Series|Cluster|DragXY|DragX|DragY
	 */
	public selectionMode: string;

	/** Sensor X Axis */
	public sensorXAxis: Object;

	/** titleStyle */
	public titleStyle: FontModel = {
		fontWeight: "bold",
		size: "large"
	}

	/** dasboardFields */
	public dasboardFields: Object = { text: "DashboardName", value: "OrganisationDashboardID" };

	/** startDate */
	public startDate: Date;

	/** endDate */
	public endDate: Date;

	/** numDays */
	public numDays: number = 1;

	/** panelData */
	public publicPanelData: any[] = [];

	/** majorTicks */
	public majorTicks: Object = {
		height: 5,
		width: 1.5,
		interval: 0.5,
		offset: 0
	};

	/** minorTicks */
	public minorTicks: Object = {
		height: 5,
		width: 0.5,
		interval: 0.25,
		offset: 0
	};

	/** panelsWithDatapoints */
	public panelsWithDatapoints: number = 0;

	/** regex */
	public regex = /^\[[0-9]*\]$/;

	/** regexDelta */
	public regexDelta = /^\[[0-9]*_DELTA\]$/;

	/** regexDelta */
	public regexCalc = /^\[[0-9]*_CALCULATED\]$/;

	/** runningThreshold */
	public runningThreshold: number = 0.05;

	/** runningPointerColour */
	public runningPointerColour: string = "lightgreen";

	/** periodPointerColour */
	public periodPointerColour: string = "lightblue";

	/** currentPointerColour */
	public livePointerColour: string = "#ee95e7ff"

	/** deltaSeriesColour */
	public deltaSeriesColour: string = "lightseagreen"

	/** deltaSeriesColour */
	public totalSeriesColour: string = "#003fb4ff"

	/** dashboardContainer */
	@ViewChild('dashboardContainer') dashboardContainer!: ElementRef<HTMLDivElement>;

	/** allLoaded */
	public allLoaded: boolean = false;

	/** shortMonths */
	private shortMonths: string[] = [
		"Jan", "Feb", "Mar", "Apr", "May", "Jun",
		"Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
	];

	/** strDuration */
	public strDuration: string = "01:00:00";

	/** maxMonths */
	private maxMonths: number = 36; // max months increased as retrieving data for long periods no longer an issue

	/** DAY */
	public DAY: number = (1 * 24 * 60 * 60 * 1000);

	/** refreshGauges */
	public refreshGauges: boolean = false;

	/** refreshCharts */
	public refreshCharts: boolean = false;

	/** destroyPanels */
	public destroyPanels: boolean = false;

	/** dashboardActive */
	public dashboardActive: boolean = true;

	/** panelViews */
	private panelViews = new Map<string, EmbeddedViewRef<any>>();

	/** initialDashboardID */
	private initialDashboardID?: number;

	/** hasQueryPeriod */
	private hasQueryPeriod: boolean = false;

	/** chartBars */
	public chartBars: { panelID: number, barSeries: boolean }[] = [];

	/** isMobile */
	public isMobile: boolean = false;

	/** mobileDashboardContainer */
	@ViewChild('mobileDashboardContainer') mobileDashboardContainer: ElementRef;

	/** isFullscreen */
	public isFullscreen: boolean = false;

	/** fullScreenAspectRatio */
	public fullScreenAspectRatio: number = 1920 / 1080;

	/** defaultColorHexValue */
	public defaultColorHexValues: { name: string, hex: string }[] = [
		{ name: "green", hex: "#008000" },
		{ name: "orange", hex: "#FFA500" },
		{ name: "red", hex: "#FF0000" }
	];

	/** computedColumns */
	public computedColumns: number = this.maxColumns;

	/** seriesColors */
	private seriesColors: { datapointID: number, color: string }[] = [];

	private lightGreenCyan: string = "#00E1B3";
	private lightGreenCyanDarker1: string = "#02735c";
	private lightGreenCyanDarker2: string = "#005244";

	private darkBlue: string = "#3366FF";
	private darkBlueDarker1: string = "#022073";
	private darkBlueDarker2: string = "#010b2b";

	private defaultColors: string[] = [
		"#FF5733", // Red
		"#33FF57", // Green
		"#3357FF", // Blue
		"#F0E130", // Yellow
		"#FF33F0", // Pink
		"#33FFF6", // Aqua
		"#FF9833", // Orange
		"#F633FF", // Purple
		"#8BFF33"  // Light Green
	];

	// Variable to track the next default color to use
	private defaultColorIndex: number = 0;

	/** useDashboardViewer */
	public useDashboardViewer: boolean = true;

	/** dbvMain */
	@ViewChild('dbv_main') dbvMain!: ElementRef;

	/** refreshWebhooks */
	public refreshWebhooks: boolean = false;

	/** webhookInterval */
	private webhookInterval: any;

	/** today - #71 */
	public today: Date = new Date();

	/** datePickerPresets - #71 */
	public datePickerPresets = DatePickerPresets;

	/** expired */
	public selectedAccountExpired: boolean = false;

	/** expiredMessage */
	public expiredMessage: string = "";

	/** customTimeRanges - Git#52 */
	public customTimeRanges: { panelID: string, startDate: Date, endDate: Date }[] = [];

	/** dashboardFullscreen */
	public dashboardFullscreen: boolean = false;

	/**
	 * Default Constructor
	 * @param auth auth  
	 * @param organisationDataService organisationDataService  
	 * @param globalFunctions globalFunctions 
	 * @param router router 
	 * @param route route 
	 * @param siteSubscriptions siteSubscriptions  
	 * @param authenticationService authenticationService 
	 * @param assetsDataService assetsDataService 
	 * @param dialog dialog 
	 * @param document document 
	*/
	constructor(
		public auth: AuthService,
		public organisationDataService: OrganisationDataService,
		public globalFunctions: GlobalFunctions,
		public router: Router,
		private route: ActivatedRoute,
		private siteSubscriptions: siteSubscriptions,
		private authenticationService: AuthenticationService,
		private assetsDataService: AssetsDataService,
		public dialog: MatDialog,
		@Inject(DOCUMENT) public document: Document,
		private vcr: ViewContainerRef
	) {
		this.checkMainDiv();
		this.subscribeToExitFullScreenEvent();
	}

	/**
	 * Page On Destroy Event Handler
	 */
	ngOnDestroy(): void {
		try {
			// Unsubscribe from all subscriptions
			this.finalise.next();
			this.finalise.complete();

			this.clearWebhookInterval(); //#65
		}
		catch { }
	}

	/** isMobileDevice */
	isMobileDevice(): boolean {
		let clientWidth = 2000;
		if (this.dbvMain) {
			clientWidth = this.dbvMain.nativeElement.clientWidth;
		}
		const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
		return /android|iPad|iPhone|iPod|windows phone/i.test(userAgent) || clientWidth < 1000;
	}

	/**
	 * checkMainDiv
	 */
	checkMainDiv() {
		if (!this.dbvMain) {
			setTimeout(() => {
				this.checkMainDiv();
			}, 100);
		} else {
			this.isMobile = this.isMobileDevice();
		}
	}

	/**
	 * ngOnInit
	 */
	ngOnInit(): void {

		this.route.queryParamMap
			.pipe(take(1))
			.subscribe(qp => {
				const dashboard = qp.get('dashboard');
				this.initialDashboardID = dashboard ? parseInt(dashboard) : undefined;
				if (qp.has("days")) {
					this.hasQueryPeriod = true;
					const value: number = parseFloat(qp.get("days")!);
					if (value < 1) {
						this.strDuration = `00:${(value * 24).toFixed(0).padStart(2, "0")}:00`;
					} else {
						this.strDuration = `${value.toString().padStart(2, "0")}:00:00`;
					}
					this.selectNumDays(this.strDuration, true);
				} else if (qp.has("from") && qp.has("to")) {
					this.hasQueryPeriod = true;
					const to: number = parseInt(qp.get("to")!);
					const from: number = parseInt(qp.get("from")!);
					this.DatePickerChange({ startDate: new Date(from), endDate: new Date(to) }, true);
				}
			});

		this.initSubscriptions();
	}

	/**
	 * initSubscriptions
	 */
	async initSubscriptions() {

		combineLatest([
			this.authenticationService.currentUser$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.route.paramMap.pipe(takeUntil(this.finalise), distinctUntilChanged())
		]).pipe(
			filter(([currentUser, paramMap]) =>
				!!currentUser &&
				paramMap.has('organisation')
			)
		).subscribe({
			next: async ([currentUser, paramMap]) => {

				await this.GetAccountInfo();

				this.thisUser = currentUser;

				if (!this.hasQueryPeriod) {
					this.endDate = new Date();
					this.startDate = new Date();
					this.startDate.setDate(this.endDate.getDate() - this.numDays);
				}

				if (this.initialDashboardID != null) {
					this.getDashboards(this.selectedAccountExpired, this.initialDashboardID);
				} else {
					this.getDashboards(this.selectedAccountExpired);
				}
			},
			error: (error) => {
				this.globalFunctions.HandleError(
					error,
					"Error: Error in one of the combined subscriptions",
					false
				);
			}
		});
	}


	async GetAccountInfo() {
		let [selectedAccount, selectedAccountGUID, selectedOrganisation, selectedOrganisationUID] = await firstValueFrom(
			combineLatest([
				this.siteSubscriptions.selectedAccount$.pipe(take(1)),
				this.siteSubscriptions.selectedAccountGUID$.pipe(take(1)),
				this.siteSubscriptions.selectedOrganisation$.pipe(take(1)),
				this.siteSubscriptions.selectedOrganisationUID$.pipe(take(1))
			]
			)
		);

		this.selectedAccount = selectedAccount;
		this.selectedAccount = this.siteSubscriptions.normalizeFeatures(this.selectedAccount) as Account;
		this.accountGUID = this.selectedAccount.GUID;
		this.organisation = selectedOrganisation;
		this.organisationUID = this.organisation.Uid;
		this.selectedAccountExpired = isExpired(this.selectedAccount.Expiry);

		if (this.selectedAccountExpired) {
			this.expiredMessage = `${this.selectedAccount.Name} Account Expired on ${formatDate(this.selectedAccount.Expiry, 'dd-MMM-yyyy', 'en-GB')}`;
		}
	}
	/**
	 * ngAfterViewInit
	 */
	ngAfterViewInit(): void {
		// this.resizeGauges();

		this.templates = [this.textPanelTemplate, this.gaugePanelTemplate, this.chartPanelTemplate,
		this.listPanelTemplate, this.shortcutPanelTemplate, this.linearGaugePanelTemplate, this.annotatedGaugePanelTemplate];

		this.hierarchyTemplates = [this.textPanelTemplate, this.gaugePanelTemplate, this.chartPanelTemplate,
		this.listPanelTemplate, this.shortcutPanelTemplate, this.watchListPanelTemplate,
		this.actionsPanelTemplate, this.rulesPanelTemplate, this.eventsPanelTemplate,
		this.linearGaugePanelTemplate, this.annotatedGaugePanelTemplate, this.webhooksPanelTemplate];

		this.setXAxis();
		this.tooltipSettings = {
			enable: true,
			enableAnimation: false,
			fadeOutDuration: 0,
			shared: true // Enables shared tooltip
		};

		// if (this.dashboardContainer) {
		// 	this.setUpObserver();
		// }
	}

	/**
	 * getDashboards
	 */
	getDashboards(defaultOnly: boolean, overrideLoadID?: number, noLoad: boolean = false) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.organisationDataService.getOrganisationDashboard$(this.accountGUID, this.organisationUID).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((dashboards) => !!dashboards),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (dashboards) => {

				this.existingDashboards = dashboards.map((x: any) => ({
					...x,
					DashboardConfig: JSON.parse(x.DashboardConfig)
				}));

				if (defaultOnly || !this.checkFeature(AccountFeatures.Custom_Dashboards)) {
					this.existingDashboards = this.existingDashboards.filter(x => x.Locked == true)
				};

				if (this.existingDashboards.length > 0) {
					if (!overrideLoadID) {
						this.selectedDashboard = this.getDefaultDashboard(this.existingDashboards);
					} else {
						this.selectedDashboard = this.existingDashboards.find((x: OrganisationDashboard) => x.OrganisationDashboardID == overrideLoadID);
						if (!this.selectedDashboard) {
							this.selectedDashboard = this.getDefaultDashboard(this.existingDashboards);
						}
					}
					if (!noLoad) {
						this.loadDashboard(this.selectedDashboard);
					}
				}
			},
			error: (error) => {
				if (error.status != 404) {
					console.error("this.organisationDataService.getOrganisationDashboard$:", error);
				}
			},
		});
	}

	/**
	 * checkFeature
	 * @param feature feature   
	 * @returns 
	 */
	checkFeature(feature: AccountFeatures): boolean {
		if (this.selectedAccount && this.selectedAccount.Features) {
			return AccountsService.checkFeature(feature, this.selectedAccount)
		}
		return false;
	}

	/**
	 * resetDashboard
	 */
	resetDashboard() {
		this.panelViews.forEach(view => view.destroy());
		this.panelViews.clear();
		if (this.dashboard) this.dashboard.removeAll();
		this.dashboardActive = false;
		this.panelInfo = [];
		if (this.dashboard) this.dashboard.panels = [];
		this.dashboardName = "";
		this.publicPanelData = [];
		this.panelsWithDatapoints = 0;
		this.allLoaded = false;
		// this.destroyPanels = true;
		this.refreshCharts = false;
		this.refreshGauges = false;
		this.defaultColorIndex = 0;
		this.seriesColors = [];
		this.clearWebhookInterval();
		setTimeout(() => {
			// this.destroyPanels = false;
			this.dashboardActive = true;
		}, 1);
	}

	/**
	 * resetData
	 */
	resetData() {
		this.publicPanelData = [];
		this.panelsWithDatapoints = 0;
		this.allLoaded = false;
	}

	/**
	 * loadDashboard
	 * @param dashboard dashboard  
	 */
	loadDashboard(dashboard: OrganisationDashboard) {
		this.resetDashboard();
		this.router.navigate([], {
			queryParams: { dashboard: dashboard.OrganisationDashboardID },
			queryParamsHandling: 'merge'
		});
		this.selectedDashboard = dashboard;
		this.dashboardName = dashboard.DashboardName;
		if (this.dashboard) this.dashboard.panels = dashboard.DashboardConfig
			.map((x) => ({ ...x.panel, content: `<div id=${x.panel.id} class="panel-host" style="width: 100%; height: 100%;"></div>` }));
		this.panelInfo = dashboard.DashboardConfig.map((x) => x.info);
		this.loadPanels(true);
	}

	/**
	 * loadPanels
	 */
	async loadPanels(render?: boolean): Promise<void> {
		await new Promise(resolve => setTimeout(resolve, 0));

		if (!this.isMobile) {
			this.calculateAspectRatio();
		} else {
			this.calculateMobileAspectRatio();
		}

		const panelPromises = this.panelInfo.map(async (x) => {
			if (render) {
				const context = { ...x.context, id: parseInt(x.id), isHierarchy: x.isHierarchy, barDefault: x.barDefault };

				if (x.template == "Chart") {
					this.chartBars.push({ panelID: parseInt(x.id), barSeries: x.barDefault ?? false });
				}

				this.renderTemplateIntoPanel(
					x.id,
					x.isHierarchy ? this.hierarchyTemplates[x.templateID] : this.templates[x.templateID],
					context
				);
			}

			await this.getPanelData(x, x.isHierarchy);
		});

		await Promise.all(panelPromises);

		// this.refreshDashboard();
	}


	/**
	 * resize
	 */
	resize() {
		this.resizeGauges();
		this.resizeCharts();
		this.refreshLists();
	}

	/**
	 * resizeGauges
	 */
	resizeGauges() {
		this.refreshGauges = true;
		setTimeout(() => {
			this.refreshGauges = false;
		}, 0);
	}

	/**
	 * resizeCharts
	 */
	resizeCharts() {
		this.refreshCharts = true;
		setTimeout(() => {
			this.refreshCharts = false;
		}, 0);
	}

	/**
	 * refreshLists
	 */
	refreshLists() {
		this.allLists.forEach((x: ListViewComponent) => {
			const parent = x.element.parentElement?.parentElement;
			const height: number = parent?.offsetHeight ?? 0;
			const width: number = parent?.offsetWidth ?? 0;
			x.height = `${height}px`;
			x.width = `${width}px`;
			x.dataBind();
			x.refresh();
		});
	}

	/** 
	 * renderTemplateIntoPanel 
	 */
	renderTemplateIntoPanel(panelId: string, template: TemplateRef<any>, data: any) {
		const host = document.getElementById(panelId)?.getElementsByClassName("panel-host")[0];
		if (!host) return;
		if (!host.classList.contains("scaling-text-height")) {
			host.classList.add("scaling-text-height");
		}

		const view = this.vcr.createEmbeddedView(template, { data });
		this.panelViews.set(panelId, view);
		view.detectChanges();

		host.appendChild(view.rootNodes[0]);
	}

	/**
	 * selectDashbord
	 * @param event event 
	 */
	selectDashboard(event: any) {
		if (event.previousItem != null) {
			// this.router.navigate([this.router.url.split("?")[0]], { queryParams: { dashboard: event.itemData.OrganisationDashboardID } });
			this.loadDashboard(event.itemData);
		}
	}

	/**
	 * calculateAspectRatio
	 */
	calculateAspectRatio() {
		if (this.dashboard) {
			if (this.isFullscreen) {
				let lowestPosition: number = 0;
				this.dashboard.panels.forEach((x: PanelModel) => {
					if (x.row && x.row > lowestPosition) {
						lowestPosition = x.row;
					}
				});
				let tallestPanel: number = 0;
				this.dashboard.panels.filter((x: PanelModel) => x.row == lowestPosition).forEach((y: PanelModel) => {
					if (y.sizeY && y.sizeY > tallestPanel) {
						tallestPanel = y.sizeY;
					}
				});
				const height = lowestPosition + tallestPanel;
				this.aspectRatio = (height * 2.1) / this.computedColumns;
			} else {
				this.aspectRatio = (this.maxRows * 2.2) / this.maxRows;
			}

			setTimeout(() => {
				this.dashboard.refresh();
				// this.refreshDashboard();
			}, 0);
		}
	}

	/**
	 * calculateMobileAspectRatio
	 */
	calculateMobileAspectRatio() {
		if (this.mobileDashboardContainer && this.dashboard) {
			if (this.isFullscreen) {
				let lowestPosition: number = 0;
				this.dashboard.panels.forEach((x: PanelModel) => {
					if (x.row && x.row > lowestPosition) {
						lowestPosition = x.row;
					}
				});
				let tallestPanel: number = 0;
				this.dashboard.panels.filter((x: PanelModel) => x.row == lowestPosition).forEach((y: PanelModel) => {
					if (y.sizeY && y.sizeY > tallestPanel) {
						tallestPanel = y.sizeY;
					}
				});
				const height = lowestPosition + tallestPanel;
				this.mobileAspectRatio = (height * 1.7) / this.computedColumns;
			} else {
				const height: number = this.mobileDashboardContainer.nativeElement.offsetHeight;
				const width: number = this.mobileDashboardContainer.nativeElement.offsetWidth;
				this.mobileAspectRatio = (width / height) * 1;
			}
			setTimeout(() => {
				this.dashboard.refresh();
				// this.refreshDashboard();
			}, 0);
			// this.mobileAspectRatio = Math.max(width, height) / Math.min(width, height);
		}
	}

	/**
	 * refreshDashboard
	 */
	refreshDashboard() {
		setTimeout(() => {
			if (this.dashboard) this.dashboard.refresh();
			setTimeout(() => {
				this.resize();
			}, 1);
		}, 1);
	}

	/**
	 * getPanelData
	 * @param panel panel  
	 */
	async getPanelData(panel: any, isHierarchy: boolean = false, refreshing: boolean = false) {
		// console.log('START', panel.id, performance.now());
		let datapoints: DataPoint[] = [];
		let kWhPanel: boolean = false;
		let flattenedNodes;
		let data: ISensorData[] = [];
		if (!isHierarchy) {
			flattenedNodes = this.flattenNodes([panel.context.nodeData], true);
			flattenedNodes.forEach((x: OrganisationDataModelNode) => datapoints.push(...x.Datapoints ?? []));
			if (panel.context.nodeData.DataPointID) {
				datapoints.push(panel.context.nodeData);
			}
		} else {
			// const assets = this.getAssetsFromHierarchyTreeNode(panel.context.nodeData);
			// for (let i: number = 0; i < assets.length; i++) {
			// 	const assetDPs: AssetDataPoint[] = await this.getAssetDataPoints(assets[i]);
			// 	datapoints.push(...assetDPs);
			// }
			datapoints = panel.context.datapoints;
		}
		if (datapoints.length > 0) {
			const kWhDPs = datapoints.filter((x: DataPoint) => x.SI == "kWh" && x.IsCumulative);
			if (kWhDPs.length > 0) {
				kWhPanel = true;
				if (kWhDPs.length != datapoints.length) {
					console.log("Panel cannot contain a mixture of kWh datapoints and non kWh datapoints");
					return;
				}
			}
			this.panelsWithDatapoints++;
		}

		// handling custom panel range - Git#52
		let startDate = this.startDate;
		let endDate = this.endDate;
		if (this.customTimeRanges.length > 0) {
			const findRange = this.customTimeRanges.find((x) => x.panelID == panel.id);
			if (findRange) {
				startDate = findRange.startDate;
				endDate = findRange.endDate;
			}
		}

		data = await this.GetDataPointValues(datapoints.map((x: DataPoint) => x.DataPointID).toString(), startDate.getTime(), endDate.getTime());

		let panelData: PanelData = {
			id: panel.id,
			metricLabels: [],
			numMetrics: panel.context.metricData.metricChoices.length,
			numberMetrics: [],
			dataMetrics: [],
			series: [],
			SI: datapoints[0]?.SI ?? "",
			metricColours: [],
			datapointMetrics: []
		}

		if (data.length > 0) {
			const FIVE_MINUTES = (1000 * 60 * 5);
			const TEN_MINUTES = FIVE_MINUTES * 2;
			let kWhData: { datapointID: number, kWhData: ISensorData[] }[] = [];
			let totalKWHData: ISensorData[] = [];
			let totalCumulativeData: ISensorData[] = [];
			let totalDeltaData: ISensorData[] = [];
			let totalSIData: ISensorData[] = [];
			let metricValues: any = undefined;
			let metricSIValues: any = undefined;
			let dataPointMaxMinValues: any = undefined;
			if (!isHierarchy) {
				let kWhSI: string = "kWh";
				datapoints.forEach((datapoint: DataPoint) => {
					if (datapoint.IsCumulative && datapoint.SI == kWhSI) {
						kWhData.push(
							{
								datapointID: datapoint.DataPointID,
								kWhData: this.calculateKWHData(datapoint.DataPointID, data)
							});
					}
				});
				if (datapoints.length > 1) {
					if (kWhData.length > 0) {
						totalKWHData = this.totaliseCalculatedData(TEN_MINUTES, kWhData);
					}
					totalCumulativeData = this.totaliseCumulativeData(data, TEN_MINUTES, datapoints);
					totalDeltaData = this.totaliseDeltaData(data, TEN_MINUTES);
				} else {
					if (kWhData.length > 0) {
						totalKWHData = kWhData[0].kWhData.map((x: ISensorData) => ({ ...x, Total_CALCULATED: x[`[${kWhData[0].datapointID}_CALCULATED]`] }));
					}
					totalCumulativeData = data.map((x: ISensorData) => ({ ...x, Total: x[`[${datapoints[0].DataPointID}]`] }));
					totalDeltaData = data.map((x: ISensorData) => ({ ...x, Total_DELTA: x[`[${datapoints[0].DataPointID}_DELTA]`] }));
				}
				metricValues = kWhPanel ? this.getDataMaxMin(totalKWHData, "Total_CALCULATED") : this.getDataMaxMin(totalDeltaData, "Total_DELTA");
			} else {
				totalSIData = this.totaliseDataBySI(data, TEN_MINUTES, datapoints);
				metricSIValues = this.getSIDataMaxMin(totalSIData, datapoints);
				dataPointMaxMinValues = this.getDatapointsDataMaxMin(data, datapoints);
			}
			if (!["Chart", "List", "Linear Gauge", "Annotated Gauge", "Gauge"].includes(panel.template)) {
				let metricDataToUse = (!isHierarchy && metricValues) ? metricValues : (isHierarchy && metricSIValues) ? metricSIValues[0] : undefined;
				if (metricDataToUse) {
					panel.context.metricData.metricChoices.forEach((metricChoice: number) => {
						const label = panel.context.metricData.metrics[metricChoice].text;
						let value: number = 0;
						let colour: string = "";
						switch (label) {
							case "Period Average":
								value = parseFloat(metricDataToUse.avg.toFixed(3));
								colour = this.periodPointerColour;
								break;
							case "Running Average":
								value = parseFloat(metricDataToUse.runningAvg.toFixed(3));
								colour = this.runningPointerColour;
								break;
							case "Live Usage":
							case "Live Value":
								if (!isHierarchy) {
									value = kWhPanel ? this.getLiveUsage(totalKWHData, "Total_CALCULATED") : this.getLiveUsage(totalDeltaData, "Total_DELTA");
								} else {
									value = parseFloat(metricDataToUse.liveUsage.toFixed(3));
								}
								colour = this.livePointerColour;
								break;
							case "Period Difference":
							case "Period Usage":
								value = this.getPeriodDifference(totalCumulativeData, "Total");
								break;
							case "Max Usage":
							case "Max Value":
								if (!isHierarchy) {
									value = this.getMaxUsage(kWhPanel ? totalKWHData : totalDeltaData, kWhPanel ? "Total_CALCULATED" : "Total");
								} else {
									value = metricDataToUse.max;
								}
								break;
						}
						panelData.metricColours?.push(colour);
						panelData.metricLabels.push(label);
						panelData.numberMetrics.push(value);
					});
				}
			} else if (panel.template == "Chart") {
				if (!isHierarchy) {
					panel.context.metricData.metricChoices.forEach((metricChoice: number) => {
						let label = panel.context.metricData.metrics[metricChoice].text;
						let sensorData: ISensorData[] = [];
						let barData: ISensorData[] = [];
						let yName: string = "";
						let colour: string = "";
						switch (label) {
							case "Total":
							case "Raw Value":
								sensorData = totalCumulativeData;
								yName = "Total";
								colour = this.totalSeriesColour;
								break;
							case "Running Value":
							case "Usage":
								sensorData = kWhPanel ? totalKWHData : totalDeltaData;
								// if (kWhPanel) {
								// barData = this.calculateDeltaChartBarData(totalKWHData, "Total_CALCULATED");
								// } else {
								barData = this.calculateDeltaChartBarData(totalDeltaData, "Total_DELTA");
								// }
								yName = kWhPanel ? "Total_CALCULATED" : "Total_DELTA";
								colour = this.deltaSeriesColour;
								break;
						}
						const series: SeriesModel = {
							animation: {
								enable: false
							},
							emptyPointSettings: { mode: "Drop" },
							type: 'Line',
							dataSource: sensorData,
							name: `Total Usage ${kWhPanel ? '(kWh)' : ''}`,
							xName: 'timestamp',
							yName: yName,
							marker: {
								visible: false,
								width: 8,
								height: 8,
								shape: 'Triangle',
								dataLabel: { "name": 'x' },
								isFilled: true
							},
							// yAxisName: `Axis${this.targetSI}`,
							fill: colour
						}
						const barSeries: SeriesModel = {
							animation: {
								enable: false
							},
							emptyPointSettings: { mode: "Drop" },
							type: 'Column',
							dataSource: barData,
							name: `Total Usage ${kWhPanel ? '(kWh)' : ''}`,
							xName: 'timestamp',
							yName: "BAR_DATA",
							marker: {
								visible: false,
								width: 8,
								height: 8,
								shape: 'Triangle',
								dataLabel: { "name": 'x' },
								isFilled: true
							},
							// yAxisName: `Axis${this.targetSI}`,
							fill: colour,
							columnSpacing: 0.2,
							columnWidth: 1
						}
						panelData.metricColours?.push(colour);
						panelData.metricLabels.push(label);
						panelData.dataMetrics.push(sensorData);
						panelData.series?.push(series);
						panelData.barSeries = barSeries;
						const chartBar = this.chartBars.find((x) => x.panelID == parseInt(panel.id));
						if (chartBar) {
							panelData.activeSeries = chartBar.barSeries ? [barSeries] : [series];
						} else {
							panelData.activeSeries = panelData.series;
						}
					});
				} else {
					datapoints.forEach((x: DataPoint) => {
						let seriesColor: string = "";
						let existingColor: boolean = false;
						let findExistingColor = this.seriesColors.find((y) => y.datapointID == x.DataPointID);
						if (findExistingColor && findExistingColor.color) {
							seriesColor = findExistingColor.color;
							existingColor = true;
						}
						if (!existingColor) {
							if (x.SI.includes('g')) {
								// Check if lightGreenCyan has already been used
								if (this.seriesColors.some(s => s.color === this.lightGreenCyan)) {
									// If used, choose a darker shade
									seriesColor = this.seriesColors.some(s => s.color === this.lightGreenCyanDarker1)
										? this.lightGreenCyanDarker2
										: this.lightGreenCyanDarker1;
								} else {
									seriesColor = this.lightGreenCyan;
								}
							} else if (x.SI.includes('C')) {
								// Check if darkBlue has already been used
								if (this.seriesColors.some(s => s.color === this.darkBlue)) {
									// If used, choose a darker shade
									seriesColor = this.seriesColors.some(s => s.color === this.darkBlueDarker1)
										? this.darkBlueDarker2
										: this.darkBlueDarker1;
								} else {
									seriesColor = this.darkBlue;
								}
							} else {
								// If no specific condition is met, use the next color in the default array
								seriesColor = this.defaultColors[this.defaultColorIndex];

								// Increment the defaultColorIndex, and loop back to 0 if we reach the end of the array
								this.defaultColorIndex = (this.defaultColorIndex + 1) % this.defaultColors.length;
							}
							this.seriesColors.push({
								datapointID: x.DataPointID,
								color: seriesColor
							});
						}
						const series: SeriesModel = {
							animation: {
								enable: false
							},
							emptyPointSettings: { mode: "Drop" },
							type: 'Line',
							dataSource: data,
							name: `${x.DPTitle}`,
							xName: 'timestamp',
							yName: `[${x.DataPointID}]`,
							marker: {
								visible: false,
								width: 8,
								height: 8,
								shape: 'Triangle',
								dataLabel: { "name": 'x' },
								isFilled: true
							},
							yAxisName: `Axis${x.SI}`,
							fill: seriesColor
						};
						// panelData.metricColours?.push(colour);
						// panelData.metricLabels.push(label);
						// panelData.dataMetrics.push(sensorData);
						panelData.series?.push(series);
						// panelData.barSeries = barSeries;
						panelData.activeSeries = panelData.series;
					});
				}
			} else if (panel.template == "List") {
				if (kWhPanel) {
					let firstRun: boolean = true;
					datapoints.forEach((dp: DataPoint) => {
						let dpMetrics: number[] = [];
						const dpData = kWhData.find((x) => x.datapointID == dp.DataPointID)?.kWhData;
						panel.context.metricData.metricChoices.forEach((metricChoice: number) => {
							const label = panel.context.metricData.metrics[metricChoice].text;
							let colour: string = "";
							let value: number = 0;
							switch (label) {
								case "Period Average":
									if (dpData) {
										value = this.getDataMaxMin(dpData, `[${dp.DataPointID}_CALCULATED]`).avg;
									}
									colour = this.periodPointerColour;
									break;
								case "Running Average":
									if (dpData) {
										value = this.getDataMaxMin(dpData, `[${dp.DataPointID}_CALCULATED]`).runningAvg;
									}
									colour = this.runningPointerColour;
									break;
								case "Live Usage":
									if (dpData) {
										value = this.getLiveUsage(dpData, `[${dp.DataPointID}_CALCULATED]`);
									}
									colour = this.livePointerColour;
									break;
								case "Period Usage":
								case "Period Difference":
									value = this.getPeriodDifference(data, `[${dp.DataPointID}]`);
									break;
								case "Max Usage":
								case "Max Value":
									if (dpData) {
										value = this.getMaxUsage(dpData, `[${dp.DataPointID}_CALCULATED]`);
									}
									break;
							}
							dpMetrics.push(value);
							if (firstRun) {
								panelData.metricColours?.push(colour);
								panelData.metricLabels.push(label);
							}
						});
						firstRun = false;
						panelData.datapointMetrics?.push({ datapoint: dp, metrics: dpMetrics });
					});
				} else {
					let firstRun: boolean = true;
					datapoints.forEach((dp: DataPoint) => {
						let dpMetrics: number[] = [];
						const dpMinMax = dataPointMaxMinValues.find((x: any) => x.datapointID == dp.DataPointID);
						panel.context.metricData.metricChoices.forEach((metricChoice: number) => {
							const label = panel.context.metricData.metrics[metricChoice].text;
							let colour: string = "";
							let value: number = 0;
							switch (label) {
								case "Period Average":
									if (dpMinMax) {
										value = dpMinMax.avg;
									}
									colour = this.periodPointerColour;
									break;
								case "Running Average":
									if (dpMinMax) {
										value = dpMinMax.runningAvg;
									}
									colour = this.runningPointerColour;
									break;
								case "Live Usage":
									if (dpMinMax) {
										value = dpMinMax.liveUsage;
									}
									colour = this.livePointerColour;
									break;
								case "Period Usage":
								case "Period Difference":
									value = this.getPeriodDifference(data, `[${dp.DataPointID}]`);
									break;
								case "Max Usage":
								case "Max Value":
									if (dpMinMax) {
										value = dpMinMax.max;
									}
									break;
							}
							dpMetrics.push(value);
							if (firstRun) {
								panelData.metricColours?.push(colour);
								panelData.metricLabels.push(label);
							}
						});
						firstRun = false;
						panelData.datapointMetrics?.push({ datapoint: dp, metrics: dpMetrics });
					});
				}
			} else if (panel.template == "Linear Gauge") {
				if (isHierarchy) {
					datapoints.forEach((dp: DataPoint) => {
						const filteredData = data.filter((x) => x[`[${dp.DataPointID}]`] != undefined);
						const liveValue = this.getLiveUsage(filteredData, `[${dp.DataPointID}]`);
						// const filteredData = data.filter((x) => x[`[${dp.DataPointID}_DELTA]`] != undefined);
						// const liveValue = this.getLiveUsage(filteredData, `[${dp.DataPointID}_DELTA]`);
						panelData.metricLabels.push("Live Value");
						panelData.numberMetrics.push(liveValue);
					});
				} else {
					datapoints.forEach((dp: DataPoint) => {
						let liveValue = 0;
						if (dp.SI == "kWh") {
							const DPkWhData = kWhData.find((x: { datapointID: number; kWhData: ISensorData[]; }) => x.datapointID == dp.DataPointID);
							if (DPkWhData) {
								liveValue = this.getLiveUsage(DPkWhData.kWhData, `[${dp.DataPointID}_CALCULATED]`);
							}
						} else {
							const filteredData = data.filter((x) => x[`[${dp.DataPointID}_DELTA]`] != undefined);
							liveValue = this.getLiveUsage(filteredData, `[${dp.DataPointID}_DELTA]`);
						}
						panelData.metricLabels.push("Live Value");
						panelData.numberMetrics.push(liveValue);
					});
				}
			} else if (panel.template == "Annotated Gauge" || panel.template == "Gauge") {
				if (isHierarchy) {
					datapoints.forEach((dp: DataPoint) => {
						const filteredData = data.filter((x) => x[`[${dp.DataPointID}]`] != undefined);
						const liveValue = this.getLiveUsage(filteredData, `[${dp.DataPointID}]`);
						// const filteredData = data.filter((x) => x[`[${dp.DataPointID}_DELTA]`] != undefined);
						// const liveValue = this.getLiveUsage(filteredData, `[${dp.DataPointID}_DELTA]`);
						panelData.metricLabels.push("Live Value");
						panelData.numberMetrics.push(liveValue);
					});
				} else {
					if (panel.context.totalise) {
						let liveValue = 0;
						if (kWhPanel) {
							liveValue = this.getLiveUsage(totalKWHData, "Total_CALCULATED");
						} else {
							liveValue = this.getLiveUsage(totalDeltaData, "Total_DELTA");
						}
						panelData.metricLabels.push("Live Value");
						panelData.numberMetrics.push(liveValue);
					} else {
						datapoints.forEach((dp: DataPoint) => {
							let liveValue = 0;
							if (dp.SI == "kWh") {
								const DPkWhData = kWhData.find((x: { datapointID: number; kWhData: ISensorData[]; }) => x.datapointID == dp.DataPointID);
								if (DPkWhData) {
									liveValue = this.getLiveUsage(DPkWhData.kWhData, `[${dp.DataPointID}_CALCULATED]`);
								}
							} else {
								const filteredData = data.filter((x) => x[`[${dp.DataPointID}_DELTA]`] != undefined);
								liveValue = this.getLiveUsage(filteredData, `[${dp.DataPointID}_DELTA]`);
							}
							panelData.metricLabels.push("Live Value");
							panelData.numberMetrics.push(liveValue);
						});
					}
				}
			}
			let metricValsToUse = (!isHierarchy && metricValues) ? metricValues : (isHierarchy && metricSIValues) ? metricSIValues[0] : undefined;
			if (metricValsToUse) {
				panelData.max = parseFloat((metricValsToUse.max || 10).toFixed(0));
				if (panelData.max == 0) panelData.max = 1;
				panelData.min = parseFloat((metricValsToUse.min).toFixed(0));
				panelData.interval = parseFloat(((panelData.max - panelData.min) / 6).toFixed(0));
				let majorTicks: Object = {
					height: 5,
					width: 1.5,
					interval: parseFloat((((metricValsToUse.max || 10) - metricValsToUse.min) / 10).toFixed(1)),
					offset: 0
				};
				panelData.majorTicks = majorTicks;
			}
			this.publicPanelData[parseInt(panel.id)] = panelData;
		} else {
			this.publicPanelData[parseInt(panel.id)] = panelData;
		}
		// console.log('END', panel.id, performance.now());
	}

	/**
	 * getPeriodAverage
	 * @param data data 
	 * @returns 
	 */
	getPeriodAverage(data: ISensorData[]): number {
		let result: number = 2;

		return result;
	}

	/**
	 * getRunningAverage
	 * @param data data 
	 * @returns 
	 */
	getRunningAverage(data: ISensorData[]): number {
		let result: number = 1;

		return result;
	}

	/**
	 * getLiveUsage
	 * @param data data 
	 * @returns 
	 */
	getLiveUsage(data: ISensorData[], label: string): number {
		if (data.length == 0) {
			return 0;
		}
		const result = <number>data[data.length - 1][label] ?? 0;
		return parseFloat(result.toFixed(3));
	}

	/** getPeriodDifference */
	getPeriodDifference(data: ISensorData[], label: string): number {
		let max: number | undefined = undefined;
		let index: number = 0;
		while (max == undefined) {
			max = <number>data[(data.length - 1) - index][label];
			if (max != undefined) {
				break;
			} else {
				index++;
			}
		}
		let min: number | undefined = undefined;
		index = 0;
		while (min == undefined) {
			min = <number>data[0 + index][label];
			if (min != undefined) {
				break;
			} else {
				index++;
			}
		}
		return parseFloat((max - min).toFixed(3));
	}

	/**
	 * getMaxUsage
	 * @param data data  
	 * @param label label 
	 */
	getMaxUsage(data: ISensorData[], label: string): number {
		let max: number = 0;
		const numData = data.map((x: ISensorData) => <number>x[label]);
		if (numData.length > 0) {
			max = Math.max(...numData);
		}
		return parseFloat(max.toFixed(3));
	}

	/**
	 * getRunningData
	 * @param data data 
	 * @returns 
	 */
	getRunningData(data: ISensorData[]): ISensorData[] {
		let result: ISensorData[] = [];

		return result;
	}

	/**
	 * getTotalData
	 * @param data data 
	 * @returns 
	 */
	getTotalData(data: ISensorData[]): ISensorData[] {
		let result: ISensorData[] = [];

		return result;
	}

	/**
	 * flattenNodes
	 * @param nodes nodes  
	 * @returns 
	 */
	flattenNodes(nodes: OrganisationDataModelNode[], includeTopLevelNodes: boolean = false): OrganisationDataModelNode[] {
		let result: OrganisationDataModelNode[] = [];

		// Check if nodes is a valid array before proceeding
		if (!Array.isArray(nodes)) {
			return result;
		}

		if (includeTopLevelNodes) {
			result.push(...nodes);
		}

		// Iterate over each item in the input hierarchy
		for (const item of nodes) {
			if (item.SubChild && item.SubChild.length > 0) {
				result = result.concat(item.SubChild);
				result = result.concat(this.flattenNodes(item.SubChild));
			}
		}

		return result;
	}

	/**
	 * Concurrent GetDataPointValues
	 * @param datapointIDs datapointIDs 
	 * @param dateStart dateStart 
	 * @param dateEnd dateEnd 
	 * @returns 
	 */
	async GetDataPointValues(
		datapointIDs: string,
		dateStart: number,
		dateEnd: number,
		assetIDs?: string
	): Promise<ISensorData[]> {
		const dataPointValues = await firstValueFrom(
			this.assetsDataService.getDataPointValues$(
				this.accountGUID,
				this.organisationUID,
				dateStart,
				dateEnd,
				datapointIDs,
				assetIDs
			)
		);

		return dataPointValues.map((dataPointValue: any) => ({
			AssetId: 0,
			timestamp: dataPointValue.Timestamp,
			sampleDate: new Date(dataPointValue.Timestamp),
			...dataPointValue.Values,
			...Object.fromEntries(
				Object.entries(dataPointValue.DeltaValues).map(
					([key, value]: any) => [key.replace("]", "") + "_DELTA]", value]
				)
			),
			...Object.fromEntries(
				Object.entries(dataPointValue.DeltaTimestamps).map(
					([key, value]: any) => [key.replace("]", "") + "_DELTA_TIME]", value]
				)
			)
		}));
	}

	/**
	 * GetDataPointValues
	 * @param datapointIDs datapointIDs  
	 * @param dateStart dateStart 
	 * @param dateEnd dateEnd 
	 * @returns 
	 */
	async GetDataPointValues2(datapointIDs: string, dateStart: any, dateEnd: any): Promise<ISensorData[]> {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		return new Promise<ISensorData[]>((resolve, reject) => {
			this.assetsDataService.getDataPointValues$(this.accountGUID, this.organisationUID, dateStart, dateEnd, datapointIDs).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				// filter((dataPointValues) => !!dataPointValues),
				tap(() => {
				}),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
				})
			).subscribe({
				next: (dataPointValues: any) => {
					try {
						let parsedData: ISensorData[];
						parsedData = dataPointValues.map((dataPointValue: any) => {
							return {
								//AssetId: dataPointValue.AssetID,
								AssetId: 0,
								timestamp: dataPointValue.Timestamp,
								sampleDate: new Date(dataPointValue.Timestamp),
								...dataPointValue.Values,
								...Object.assign(
									{},
									...Object.entries(dataPointValue.DeltaValues).map(([key, value]: any) => {
										return { [key.replace("]", "") + "_DELTA" + "]"]: value };
									})
								),
								...Object.assign(
									{},
									...Object.entries(dataPointValue.DeltaTimestamps).map(([key, value]: any) => {
										return { [key.replace("]", "") + "_DELTA_TIME" + "]"]: value };
									})
								)
							}
						});
						resolve(parsedData);

					} catch (error) {
						console.error(error);
						reject([]);
					}
				},
				error: (error) => {
					this.globalFunctions.HandleError(error, "Error: this.AssetsDataService.getAssetDataPointValues$", false)
					reject([]);
				},
				//complete: () => { console.log("this.AssetsDataService.getAssetDataPointValues$.subscribe: Complete"); }
			});
		});
	}

	/**
	 * calculateKWHData
	 */
	calculateKWHData(datapointID: number, parsedData: ISensorData[]): ISensorData[] {
		let calculatedData: ISensorData[] = [];
		for (let i: number = 1; i < parsedData.length; i++) {
			const data = parsedData[i];
			const kWhValue = this.getUsageValue(data, datapointID);
			if (kWhValue != undefined) {
				calculatedData.push({
					timestamp: data.timestamp,
					sampleDate: data.sampleDate,
					[`[${datapointID}_CALCULATED]`]: kWhValue
				});
			}
		}
		return calculatedData;
	}

	/**
	 * getUsageValue
	 * @param data data 
	 * @returns 
	 */
	getUsageValue(data: ISensorData, datapointID: number): number | undefined {
		let result = undefined;
		let deltaTimestamp = data[`[${datapointID}_DELTA_TIME]`];
		let deltaValue = data[`[${datapointID}_DELTA]`];
		let factor: number = 60 * 60 * 1000; //hour factor
		if (deltaTimestamp && (deltaValue != undefined)) {
			result = (factor / (<number>deltaTimestamp)) * <number>deltaValue;
		}
		return result;
	}

	/**
	 * totaliseCalculatedData
	 * @param FIVE_MINUTES FIVE_MINUTES 
	 */
	totaliseCalculatedData(FIVE_MINUTES: number, kWhData: { datapointID: number, kWhData: ISensorData[] }[]): ISensorData[] {
		let totalCalculatedData: ISensorData[] = [];
		for (let start: number = this.startDate.getTime(); start < this.endDate.getTime(); start += FIVE_MINUTES) {
			const end = start + FIVE_MINUTES;
			let data: ISensorData[] = [];
			kWhData.forEach((x) => {
				x.kWhData.forEach((y: ISensorData) => {
					if (y.timestamp < end && y.timestamp >= start) {
						data.push(y);
					}
				})
			});
			let avgValues: [string, number, number][] = [];
			data.forEach((y: ISensorData) => {
				Object.entries(y).forEach(item => {
					if (this.regexCalc.test(item[0])) {
						let existingItem = avgValues.find(av => av[0] == item[0]);
						if (existingItem) {
							existingItem[1] += <number>item[1];
							existingItem[2]++;
						} else {
							avgValues.push([item[0], <number>item[1], 1]);
						}
					}
				});
			});
			let timestamp = end - ((end - start) / 2);
			let total: number = 0;
			avgValues.map(x => x[1] / x[2]).forEach(x => total += x);
			if (data.length > 0) {
				totalCalculatedData.push({
					timestamp: timestamp,
					sampleDate: new Date(timestamp).toString(),
					...{ "Total_CALCULATED": total }
				});
			}
		}
		return totalCalculatedData;
	}

	/**
	 * getKWHDataMaxMin
	 * @returns 
	 */
	getDataMaxMin(inputData: ISensorData[], label: string): { max: number, min: number, avg: number, runningAvg: number } {
		let result = { max: 0, min: 999999, avg: 0, runningAvg: 0 };
		let valueCount: number = 0;
		let runningValueCount: number = 0;
		inputData.forEach(item => {
			const value = <number>item[label];
			if (value != undefined) {
				valueCount++;
				runningValueCount = value > this.runningThreshold ? runningValueCount += 1 : runningValueCount;
				result = {
					max: value > result.max ? value : result.max,
					min: value < result.min ? value : result.min,
					avg: result.avg += value,
					runningAvg: value > this.runningThreshold ? result.runningAvg += value : result.runningAvg
				}
			}
		});
		result.avg /= valueCount != 0 ? valueCount : 1;
		result.runningAvg /= runningValueCount != 0 ? runningValueCount : 1;
		result.max = parseFloat(result.max.toFixed(3));
		result.min = parseFloat(result.min.toFixed(3));
		result.avg = parseFloat(result.avg.toFixed(3));
		result.runningAvg = parseFloat(result.runningAvg.toFixed(3));
		return result;
	}

	/**
	 * getSIDataMaxMin
	 * @returns 
	 */
	getSIDataMaxMin(inputData: ISensorData[], datapoints: DataPoint[]): { SI: string, max: number, min: number, avg: number, runningAvg: number, liveUsage: number }[] {
		let results: {
			SI: string,
			max: number,
			min: number,
			avg: number,
			runningAvg: number,
			valueCount: number,
			runningValueCount: number,
			liveUsage: number,
			liveUsageTimestamp: number
		}[] = [];
		const SIs: string[] = datapoints.map((x: DataPoint) => (x.SI));
		inputData.forEach(x => {
			Object.entries(x).forEach((item) => {
				if (SIs.includes(item[0])) {
					let existingResult = results.find((res) => res.SI == item[0]);
					if (!existingResult) {
						existingResult = {
							SI: item[0],
							max: 0,
							min: Infinity,
							avg: 0,
							runningAvg: 0,
							valueCount: 0,
							runningValueCount: 0,
							liveUsage: 0,
							liveUsageTimestamp: 0
						};
						results.push(existingResult);
					}
					const value = <number>item[1];
					if (value != undefined) {
						existingResult.valueCount++;
						existingResult.runningValueCount = value > this.runningThreshold ? existingResult.runningValueCount += 1 : existingResult.runningValueCount;
						existingResult.max = value > existingResult.max ? value : existingResult.max;
						existingResult.min = value < existingResult.min ? value : existingResult.min;
						existingResult.avg += value;
						existingResult.runningAvg = value > this.runningThreshold ? existingResult.runningAvg += value : existingResult.runningAvg;
						existingResult.liveUsage = x.timestamp > existingResult.liveUsageTimestamp ? value : existingResult.liveUsage
						existingResult.liveUsageTimestamp = x.timestamp > existingResult.liveUsageTimestamp ? x.timestamp : existingResult.liveUsageTimestamp
					}
				}
			});
		});
		results.forEach((result) => {
			result.avg /= result.valueCount || 1;
			result.runningAvg /= result.runningValueCount || 1;
			result.max = parseFloat(result.max.toFixed(3));
			result.min = parseFloat(result.min.toFixed(3));
			result.avg = parseFloat(result.avg.toFixed(3));
			result.runningAvg = parseFloat(result.runningAvg.toFixed(3));
			result.liveUsage = parseFloat(result.liveUsage.toFixed(3));
		})
		return results;
	}

	/**
	 * getDatapointsDataMaxMin
	 * @param inputData inputData 
	 * @param datapoints datapoints 
	 * @returns 
	 */
	getDatapointsDataMaxMin(inputData: ISensorData[], datapoints: DataPoint[]): { datapointID: any, max: number, min: number, avg: number, runningAvg: number, liveUsage: number }[] {
		let results: {
			datapointID: any,
			max: number,
			min: number,
			avg: number,
			runningAvg: number,
			valueCount: number,
			runningValueCount: number,
			liveUsage: number,
			liveUsageTimestamp: number
		}[] = [];
		datapoints.forEach((datapoint: DataPoint) => {
			const dpData = inputData.map((x) => <number>x[`[${datapoint.DataPointID}]`]).filter((x) => x != undefined && x != null);
			const sum = dpData.reduce((prev, curr) => prev + curr, 0);
			const avg = sum / dpData.length;
			const runningDpData = dpData.filter((x) => x >= this.runningThreshold);
			const runningLength = runningDpData.length;
			const runningSum = runningDpData.reduce((prev, curr) => prev + curr, 0);
			const runningAvg = runningSum / runningLength;
			let liveUsage = 0;
			let liveUsageTimestamp = 0;
			for (let i: number = inputData.length - 1; i > 0; i--) {
				const val = inputData[i][`[${datapoint.DataPointID}]`];
				if (val != undefined && val != null) {
					liveUsage = <number>val;
					liveUsageTimestamp = inputData[i].timestamp;
					break;
				}
			}
			let result = {
				datapointID: datapoint.DataPointID,
				max: parseFloat(Math.max(...dpData).toFixed(3)),
				min: parseFloat(Math.min(...dpData).toFixed(3)),
				avg: parseFloat(avg.toFixed(3)),
				runningAvg: parseFloat(runningAvg.toFixed(3)),
				valueCount: dpData.length,
				runningValueCount: runningLength,
				liveUsage: parseFloat(liveUsage.toFixed(3)),
				liveUsageTimestamp: liveUsageTimestamp
			};
			results.push(result);
		});
		return results;
	}

	/**
	 * totaliseCumulativeData
	 * @param FIVE_MINUTES FIVE_MINUTES 
	 */
	totaliseCumulativeData2(inputData: ISensorData[], interval: number, datapoints: DataPoint[]): ISensorData[] {
		let result: ISensorData[] = [];
		let seperatedCumulativeData: { datapoint: DataPoint, data: ISensorData[] }[] = [];
		datapoints.forEach((datapoint: DataPoint) => {
			let bucketedDpData: ISensorData[] = [];
			for (let start: number = this.startDate.getTime(); start < this.endDate.getTime(); start += interval) {
				const end = start + interval;
				let data = inputData.filter((x: ISensorData) => x.timestamp < end && x.timestamp > start);
				let reading: any = undefined;
				data.forEach((dataItem: ISensorData) => {
					Object.entries(dataItem).forEach(entry => {
						if (this.regex.test(entry[0]) && entry[0] == `[${datapoint.DataPointID}]`) {
							if (reading == undefined) {
								reading = dataItem;
							} else if (reading.timestamp < dataItem.timestamp) {
								reading = dataItem;
							}
						}
					});
				});
				if (reading != undefined) {
					bucketedDpData.push({
						timestamp: reading.timestamp,
						sampleDate: reading.sampleDate,
						bucketed_data: reading[`[${datapoint.DataPointID}]`]
					});
				}
			}
			seperatedCumulativeData.push({ datapoint: datapoint, data: bucketedDpData });
		});

		let minLength: number = Math.min(...seperatedCumulativeData.map((x) => x.data.length));

		for (let i: number = 0; i < minLength; i++) {
			let total: number = 0;
			let timestamp: number = seperatedCumulativeData[0].data[i].timestamp;
			seperatedCumulativeData.forEach((x) => {
				if (x.data[i]) {
					total += <number>(x.data[i]["bucketed_data"] ?? 0);
				}
			});
			result.push({
				timestamp: timestamp,
				sampleDate: new Date(timestamp).toString(),
				...{ "Total": total }
			});
		}

		return result;
	}

	/**
	 * Improved Function
	 * @param inputData inputData 
	 * @param interval interval 
	 * @param datapoints datapoints 
	 * @returns 
	 */
	totaliseCumulativeData(
		inputData: ISensorData[],
		interval: number,
		datapoints: DataPoint[]
	): ISensorData[] {

		const result: ISensorData[] = [];
		const startTime = this.startDate.getTime();
		const endTime = this.endDate.getTime();
		const bucketCount = Math.ceil((endTime - startTime) / interval);

		const dpKeys = datapoints.map(dp => `[${dp.DataPointID}]`);

		const perDpBuckets = datapoints.map(() =>
			new Array<{ timestamp: number; value: number } | null>(bucketCount).fill(null)
		);

		for (const row of inputData) {
			const ts = row.timestamp;
			if (ts <= startTime || ts >= endTime) continue;

			const bucketIndex = Math.floor((ts - startTime) / interval);

			for (let i = 0; i < dpKeys.length; i++) {
				const key = dpKeys[i];
				const value = row[key] as number | undefined;

				if (value === undefined) continue;

				const existing = perDpBuckets[i][bucketIndex];
				if (!existing || existing.timestamp < ts) {
					perDpBuckets[i][bucketIndex] = { timestamp: ts, value };
				}
			}
		}

		const lastValues: number[] = new Array(perDpBuckets.length).fill(0);
		const lastTimestamps: number[] = new Array(perDpBuckets.length).fill(0);

		let minLength = Infinity;
		for (const buckets of perDpBuckets) {
			const len = buckets.length; // don't filter nulls
			minLength = Math.min(minLength, len);
		}

		for (let i = 0; i < minLength; i++) {
			let total = 0;
			let timestamp = 0;

			for (let dp = 0; dp < perDpBuckets.length; dp++) {
				const b = perDpBuckets[dp][i];

				if (b) {
					lastValues[dp] = b.value;
					lastTimestamps[dp] = b.timestamp;
				}

				total += lastValues[dp];
				timestamp = Math.max(timestamp, lastTimestamps[dp]);
			}

			result.push({
				timestamp,
				sampleDate: new Date(timestamp).toString(),
				Total: total
			});
		}
		return result;
	}


	/**
	 * totaliseDeltaData
	 * @param FIVE_MINUTES FIVE_MINUTES  
	 */
	totaliseDeltaData(inputData: ISensorData[], FIVE_MINUTES: number): ISensorData[] {
		let result: ISensorData[] = [];
		for (let start: number = this.startDate.getTime(); start < this.endDate.getTime(); start += FIVE_MINUTES) {
			const end = start + FIVE_MINUTES;
			let data = inputData.filter((x: ISensorData) => x.timestamp < end && x.timestamp > start);
			let avgValues: [string, number, number][] = [];
			data.forEach((y: ISensorData) => {
				Object.entries(y).forEach(item => {
					if (this.regexDelta.test(item[0])) {
						let existingItem = avgValues.find(av => av[0] == item[0]);
						if (existingItem) {
							existingItem[1] += <number>item[1];
							existingItem[2]++;
						} else {
							avgValues.push([item[0], <number>item[1], 1]);
						}
					}
				});
			});
			let timestamp = end - ((end - start) / 2);
			let total: number = 0;
			avgValues.map(x => x[1] / x[2]).forEach(x => total += x);
			if (data.length > 0) {
				result.push({
					timestamp: timestamp,
					sampleDate: new Date(timestamp).toString(),
					...{ "Total_DELTA": total }
				});
			}
		}
		return result;
	}

	/**
	 * totaliseDataBySI
	 * @param FIVE_MINUTES FIVE_MINUTES  
	 */
	totaliseDataBySI(inputData: ISensorData[], FIVE_MINUTES: number, datapoints: DataPoint[]): ISensorData[] {
		let result: ISensorData[] = [];
		const DPSIs: { dpKey: string, SI: string }[] = datapoints.map((x: DataPoint) => ({ dpKey: `[${x.DataPointID}]`, SI: x.SI }));
		for (let start: number = this.startDate.getTime(); start < this.endDate.getTime(); start += FIVE_MINUTES) {
			const end = start + FIVE_MINUTES;
			let data = inputData.filter((x: ISensorData) => x.timestamp < end && x.timestamp > start);
			let SItotals: { SI: string, total: number, count: number }[] = [];
			data.forEach((y: ISensorData) => {
				Object.entries(y).forEach((item) => {
					const DPSI = DPSIs.find((x) => x.dpKey == item[0]);
					if (DPSI) {
						let existingValue = SItotals.find((x) => x.SI == DPSI.SI);
						if (existingValue) {
							existingValue.total += <number>item[1];
							existingValue.count++;
						} else {
							SItotals.push({ SI: DPSI.SI, total: <number>item[1], count: 1 });
						}
					}
				});
			});
			let timestamp = end - ((end - start) / 2);
			if (data.length > 0) {
				SItotals.forEach((x) => {
					result.push({
						timestamp: timestamp,
						sampleDate: new Date(timestamp).toString(),
						...{ [`${x.SI}`]: parseFloat((x.total / x.count || 1).toFixed(3)) }
					});
				});
			}
		}
		return result;
	}

	/**
	 * Add a section to the graph
	 * @param lines stripLine Definition
	 */
	setXAxis(lines: object[] = []): void {

		/* Sensor Graph specific variables */
		this.sensorXAxis = {
			// title: 'Date',
			valueType: "DateTime", //'Category',
			intervalType: this.numDays > 2 ? "Days" : "Hours",
			// intervalType: "Days",
			// intervalType: "Hours",
			// interval: 1,
			labelFormat: this.numDays > 2 ? "dd-MMM" : "HH:mm dd-MMM",
			// labelFormat: "HH:mm:ss dd-MMM",
			// labelIntersectAction: "Rotate45",
			// labelRotation: 35,
			// labelRotation: 90,
			majorTickLines: { visible: false },
			minorTickLines: { visible: false },
			majorGridLines: { visible: false },
			minorGridLines: { visible: false },
			// stripLines: lines

		};
	}

	/**
	 * editDashboard
	 */
	editDashboard() {
		if (this.selectedDashboard) {
			this.router.navigate([`/setup/dashboard-management/${this.organisationUID}`], {
				queryParams: { dashboard: this.selectedDashboard.OrganisationDashboardID }
			});
		}
	}

	/**
	 * toggleShowDeltaBars
	 */
	toggleShowDeltaBars(panelData: any) {
		const data = this.publicPanelData[panelData.id];
		if (data.activeSeries) {
			if (data.activeSeries.includes(data.series![0])) {
				data.activeSeries = [data.barSeries!];
			} else {
				data.activeSeries = data.series;
			}
		}
	}

	/**
	 * calculateDeltaChartBarData
	 * @param data data 
	 * @param label label 
	 * @returns 
	 */
	calculateDeltaChartBarData(data: ISensorData[], label: string): any[] {
		let result: any[] = [];
		let numWindows: number;
		let hourSeparator: boolean = false;
		const DAY = (24 * 60 * 60 * 1000);
		const HOUR = DAY / 24;
		let numDays: number = this.numDays;
		if (this.strDuration != "custom") {
			numDays = parseInt(this.strDuration.split(":")[0]) || parseInt(this.strDuration.split(":")[1]) / 24;
		}
		numWindows = numDays > 2 ? numDays : numDays * 24;
		hourSeparator = numDays <= 2;
		for (let i: number = 0; i < numWindows; i++) {
			const startDateOnHour = new Date(this.startDate.getTime());
			startDateOnHour.setMinutes(0);
			startDateOnHour.setSeconds(0);
			if (!hourSeparator) {
				startDateOnHour.setHours(0);
			}
			let start: number = 0;
			if (this.strDuration != "custom") {
				start = startDateOnHour.getTime() + (!hourSeparator ? DAY * (i + 1) : HOUR * (i + 1));
			} else {
				start = i == 0 ? startDateOnHour.getTime() : startDateOnHour.getTime() + (!hourSeparator ? DAY * (i) : HOUR * (i));
			}
			const end = !hourSeparator ? start + DAY : start + HOUR;
			const windowData: number[] = data.filter((x: ISensorData) => x.timestamp > start && x.timestamp <= end).map((x: ISensorData) => <number>x[label]);
			let totalForWindow: number = 0;
			windowData.forEach((x: number) => totalForWindow += x);
			let timestamp = end - (!hourSeparator ? DAY / 2 : HOUR / 2);
			let tsDate: Date = new Date(timestamp);
			if (!hourSeparator) {
				tsDate.setHours(12);
			}
			tsDate.setMinutes(0);
			tsDate.setSeconds(0);
			// timestamp = tsDate.getTime();
			result.push({
				timestamp: this.getBarTimestamp(hourSeparator, start, end),
				sampleDate: new Date(timestamp).toString(),
				BAR_DATA: totalForWindow
			});
		}
		return result;
	}

	/**
	 * calculateDeltaChartBarData
	 * @param data data 
	 * @param label label 
	 * @returns 
	 */
	calculateDeltaChartBarData2(data: ISensorData[], label: string): ISensorData[] {
		let result: ISensorData[] = [];
		let numWindows: number;
		let hourSeparator: boolean = false;
		const DAY = (24 * 60 * 60 * 1000);
		const HOUR = DAY / 24;
		numWindows = this.numDays > 2 ? this.numDays : this.numDays * 24;
		hourSeparator = this.numDays <= 2;

		for (let i: number = 0; i < numWindows; i++) {
			const start = i == 0 ? this.startDate.getTime() : this.startDate.getTime() + (!hourSeparator ? DAY * (i + 1) : HOUR * (i + 1));
			const end = !hourSeparator ? start + DAY : start + HOUR;
			const windowData: number[] = data.filter((x: ISensorData) => x.timestamp > start && x.timestamp <= end).map((x: ISensorData) => <number>x[label]);
			let totalForWindow: number = 0;
			windowData.forEach((x: number) => totalForWindow += x);
			let timestamp = end - (!hourSeparator ? DAY / 2 : HOUR / 2);
			let tsDate: Date = new Date(timestamp);
			if (!hourSeparator) {
				tsDate.setHours(12);
			}
			tsDate.setMinutes(0);
			tsDate.setSeconds(0);
			timestamp = tsDate.getTime();
			result.push({
				timestamp: timestamp,
				sampleDate: new Date(timestamp).toString(),
				BAR_DATA: totalForWindow
			});
		}
		return result;
	}

	/**
	 * setUpObserver
	 */
	setUpObserver() {
		if (!this.allLoaded) {
			setTimeout(() => {
				this.setUpObserver();
			}, 100);
		} else {
			const observer = new ResizeObserver(entries => {
				for (const entry of entries) {
					this.resize();
				}
			});

			observer.observe(this.dashboardContainer.nativeElement);
		}
	}

	/** getShortDate */
	getShortDate(date: Date): string {
		let result: string = "";
		result += date.getDate();
		result +=
			" " +
			this.shortMonths[date.getMonth()] +
			" " +
			date.getHours().toString().padStart(2, "0") +
			":" +
			date.getMinutes().toString().padStart(2, "0");

		return result;
	}

	/**
	 * convertEpoch
	 * @param epoch epoch 
	 * @returns
	 */
	convertEpoch(epoch: number): string {
		/** Use the Global Function */
		return convertEpochToDateString(epoch);
	}

	/**
	 * DatePickerChange
	 * @param event event 
	 */
	DatePickerChange(event: any, initialCall: boolean = false) {
		try {
			const date1 = new Date(event.startDate.getTime());
			const date2 = new Date(event.endDate.getTime());

			const diffInMs = Math.abs(date2.getTime() - date1.getTime());
			const diffInMonths = diffInMs / (1000 * 60 * 60 * 24 * 30.44);

			if (diffInMonths < this.maxMonths) {
				this.startDate = event.startDate;
				this.endDate = event.endDate

				if (this.startDate && this.endDate) {
					/** Add a day to retrieve data for last day too */
					if (!initialCall) {
						this.endDate.setDate(this.endDate.getDate() + 1);
					}

					/** Reset Duration Search */
					this.strDuration = "custom";
					this.router.navigate([], {
						queryParams: {
							dashboard: this.selectedDashboard?.OrganisationDashboardID,
							from: this.startDate.getTime(),
							to: this.endDate.getTime()
						}
					});

					this.numDays = parseFloat(((this.endDate.getTime() - this.startDate.getTime()) / this.DAY).toFixed(2));

					if (!initialCall) {
						this.resetData();
						this.loadPanels();
					}
				}
			}
		}
		catch (error) { this.globalFunctions.HandleError(error, "DatePickerChange Error: Unknown", false) }
	}

	/**
	 * selectNumDays
	 * @param event event 
	 */
	selectNumDays(sDuration: string, initialCall: boolean = false) {
		this.strDuration = sDuration

		let parts: any[] = this.strDuration.split(":")
		this.endDate = new Date()
		this.startDate = new Date()
		this.startDate.setDate(this.startDate.getDate() - parts[0]);
		this.startDate.setHours(this.startDate.getHours() - parts[1]);
		this.startDate.setMinutes(this.startDate.getMinutes() - parts[2]);

		this.numDays = parseInt(parts[0]) || parseFloat((parseInt(parts[1]) / 24).toFixed(2));
		this.router.navigate([], {
			queryParams: { dashboard: this.selectedDashboard?.OrganisationDashboardID, days: this.numDays }
		});

		if (!initialCall) {
			this.resetData();
			this.loadPanels();
		}
	}

	/**
	 * addFloorPanel
	 */
	addFloorPanel() {
		if (this.dashboard) {
			setTimeout(() => {
				this.dashboard.addPanel({
					id: '__floor__',
					row: this.maxRows,
					col: 0,
					sizeX: 10,
					sizeY: 1,
					enabled: false
				});
			}, 1);
		}
	}

	/**
	 * respondToShortcutClick
	 * @param event event 
	 */
	respondToShortcutClick(event: OrganisationDashboard) {
		this.loadDashboard(event);
	}

	/**
	 * respondToIntervalFinish
	 * @param event event 
	 */
	respondToIntervalFinish(event: any) {
		if (this.strDuration != "custom" && parseInt(this.strDuration.split(":")[0]) <= 2) {
			let findPanel = this.panelInfo.find((x) => x.id == event.id);
			if (findPanel) {
				this.endDate = new Date();
				this.startDate = new Date();
				if (this.numDays >= 1) {
					this.startDate.setDate(this.endDate.getDate() - this.numDays);
				} else {
					this.startDate.setHours(this.endDate.getHours() - parseInt((this.numDays * 24).toFixed(0)))
				}
				this.getPanelData(findPanel, findPanel.isHierarchy, true);
			}
		}
	}

	/**
	 * flattenSubLocations
	 * @param hierarchy hierarchy 
	 * @returns 
	 */
	flattenSubLocations(hierarchy: OrganisationsHierarchy[], includeTopLevel: boolean = false): any[] {
		let result: any[] = [];

		// Check if hierarchy is a valid array before proceeding
		if (!Array.isArray(hierarchy)) {
			return result;
		}

		if (includeTopLevel) {
			result.push(...hierarchy);
		}

		// Iterate over each item in the input hierarchy
		for (const item of hierarchy) {
			// Check if the item has a non-empty 'Asset' property
			if (item.SubChild && item.SubChild.length > 0) {
				result = result.concat(item.SubChild);
			}

			// Recursively add all 'Asset' properties of the item's subchildren
			result = result.concat(this.flattenSubLocations(item.SubChild));
		}

		return result;
	}

	/**
	 * getAssetsFromHierarchyTreeNode
	 * @param treeNode treeNode 
	 */
	getAssetsFromHierarchyTreeNode(treeNode: OrganisationsHierarchy): LevelAsset[] {
		let result: LevelAsset[] = [];
		const flattenedLocations = this.flattenSubLocations([treeNode], true);
		flattenedLocations.forEach((x) => {
			if (this.isParsableAsNumber(x.Uid)) {
				result.push(x)
			}
		})
		return result;
	}

	/**
	 * Check if string can be parsed as int
	 * @param str
	 * @returns
	 */
	isParsableAsNumber(str: any): boolean {
		const parsed = Number(str);
		return !isNaN(parsed);
	}

	/**
	 * 	getAssetDataPoints
	 * @param asset asset 
	 */
	getAssetDataPoints(asset: LevelAsset): Promise<AssetDataPoint[]> {
		return new Promise<AssetDataPoint[]>((resolve, reject) => {
			const fetchDataSubject = new Subject<void>();

			this.assetsDataService.getAssetDataPoints$(this.accountGUID, this.organisationUID, asset.AssetID).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				filter((datapoints) => !!datapoints),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
				})
			).subscribe({
				next: (datapoints) => {
					resolve(datapoints);
				},
				error: (error) => {
					reject();
				},
			});
		});
	}

	/**
	 * updateChartBars
	 * @param id id 
	 * @param isBar isBar 
	 */
	updateChartBars(id: number, isBar: boolean) {
		let _chartBar = this.chartBars.find((x) => x.panelID == id);
		if (_chartBar) {
			_chartBar.barSeries = isBar;
		} else {
			this.chartBars.push({ panelID: id, barSeries: isBar });
		}
	}

	/**
	 * getBarTimestamp
	 * @param hourSeparator hourSeparator  
	 * @param start start 
	 * @param end end 
	 */
	getBarTimestamp(hourSeparator: boolean, start: number, end: number): string {
		let result: string = "";
		const startDate = new Date(start);
		const endDate = new Date(end);
		result = hourSeparator
			? `${startDate.getDate()} 
			${this.shortMonths[startDate.getMonth()]}
			${startDate.getHours().toString().padStart(2, '0')}:00 
			- 
			${end <= new Date().getTime() ? endDate.getHours().toString().padStart(2, '0') + ':00' : 'Now'}`
			: `${startDate.toDateString().split(" ").slice(0, 3).join(" ")}`;
		return result;
	}

	/**
	 * getDefaultDashboard
	 * @param dashboards dashboards 
	 */
	getDefaultDashboard(dashboards: OrganisationDashboard[]): OrganisationDashboard {

		/** Return the Default system locked dashboard */
		if (this.selectedAccountExpired || !this.checkFeature(AccountFeatures.Custom_Dashboards)) {
			const DefaultDashboard = dashboards.find((x: OrganisationDashboard) => x.Locked);
			if (DefaultDashboard) return DefaultDashboard;
			return dashboards[0];
		};

		const userDefaultDashboard = dashboards.find((x: OrganisationDashboard) => x.UserDefault);
		if (userDefaultDashboard) return userDefaultDashboard;
		const organisationDefaultDashboard = dashboards.find((x: OrganisationDashboard) => x.DefaultDashboard);
		if (organisationDefaultDashboard) return organisationDefaultDashboard;
		return dashboards[0];
	}

	/**
	 * setAsDefault
	 */
	setAsDefault() {
		if (!this.selectedDashboard) return;

		const fetchDataSubject = new Subject<void>();

		this.organisationDataService.postUserDefaultDashboard$(this.accountGUID, this.organisationUID, this.selectedDashboard?.OrganisationDashboardID).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((result) => !!result),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (result) => {
				this.globalFunctions.DisplaySnackBar({
					message: "Dashboard Set as Default",
					duration: 2000,
					action: ""
				});
				this.getDashboards(this.selectedAccountExpired, 0, true);
			},
			error: (error) => {
				this.globalFunctions.HandleError(error);
			},
		});
	}

	/**
	 * trueFullscreen
	 * @param element element  
	 */
	trueFullscreen(element: HTMLElement, fullscreen: boolean) {

		this.isFullscreen = fullscreen;
		this.dashboardFullscreen = fullscreen;
		if (fullscreen) {
			element.requestFullscreen();
		}
		else {
			if (document.fullscreenElement) { //issue `#105
				document.exitFullscreen();
			}
			this.computedColumns = this.maxColumns;
			setTimeout(() => {
				this.loadDashboard(this.selectedDashboard!);
			}, 1000);
		}
		setTimeout(() => {
			if (this.isFullscreen) this.calculateFullScreenAspectRatio(element);
			// if (!this.isMobile) {
			// 	this.calculateAspectRatio();
			// } else {
			// 	this.calculateMobileAspectRatio();
			// }
			setTimeout(() => {
				if (this.isFullscreen) {
					this.refreshDashboard();
					// this.resize();
				}
			}, 100);
		}, 500);
	}

	/**
	 * calculateFullScreenAspecRatio
	 */
	calculateFullScreenAspectRatio(element: HTMLElement) {
		let currentFurthestRightPosition: number = 0;
		let currentLowestPosition: number = 0;
		if (this.dashboard) {
			this.dashboard.panels.forEach((panel: PanelModel) => {
				const val = panel.col! + panel.sizeX!;
				const val2 = panel.row! + panel.sizeY!;
				if (val > currentFurthestRightPosition) {
					currentFurthestRightPosition = val;
				}
				if (val2 > currentLowestPosition) {
					currentLowestPosition = val2;
				}
			});
		}
		this.computedColumns = currentFurthestRightPosition;
		if (!this.isMobile) {
			this.calculateAspectRatio();
		} else {
			this.calculateMobileAspectRatio();
		}
	}

	/**
	 * respondToBorderChange
	 * @param div div 
	 * @param colour colour 
	 */
	respondToBorderChange(div: HTMLElement, colour: string) {
		if (colour == "") {
			div.style.border = "";
			div.style.backgroundColor = "inherit";
		} else {
			div.style.border = `3px solid ${colour}`;
			div.style.borderRadius = 'var(--default-dashboard-border-radius)';
			let hex: string = "";
			if (!colour.includes("#")) {
				hex = this.getDefaultColorHexValue(colour);
			} else {
				hex = colour;
			}
			let rgb = this.convertHexToRGB(hex);
			div.style.backgroundColor = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.25)`;
		}
	}

	/**
	 * convertHexToRGB
	 * @param hex hex 
	 */
	convertHexToRGB(hex: string): { r: number, g: number, b: number } {
		var bigint = parseInt(hex.slice(1, 7), 16);
		var r = (bigint >> 16) & 255;
		var g = (bigint >> 8) & 255;
		var b = bigint & 255;

		return { r: r, g: g, b: b };
	}

	/**
	 * getDefaultColorHexValue
	 * @param color color 
	 */
	getDefaultColorHexValue(color: string): string {
		let result: string = "";
		const _hex = this.defaultColorHexValues.find((x) => x.name == color)?.hex;
		if (_hex) result = _hex;
		return result;
	}

	/**
	 * startWebhookInterval
	 */
	startWebhookInterval() {
		this.webhookInterval = interval((0.5 * 60000)).pipe(takeUntil(this.finalise)).subscribe(() => {
			this.refreshWebhooks = true;
			setTimeout(() => {
				this.refreshWebhooks = false;
			}, 100);
		});
	}

	/**
	 * clearWebhookInterval
	 */
	clearWebhookInterval() {
		if (this.webhookInterval) {
			clearInterval(this.webhookInterval);  // Clear the interval if it's set
			this.webhookInterval = null;  // Reset to null to prevent memory leaks
		}
	}

	/**
	 * subscribeToExitFullScreenEvent - issue `#105
	 */
	subscribeToExitFullScreenEvent() {
		const exitHandler = () => {
			// only handle fullscreen exit events from sources that are not the button
			if (!document.fullscreenElement && this.isFullscreen) {
				this.trueFullscreen(this.dbvMain.nativeElement, false);
			}
		}

		document.addEventListener('fullscreenchange', exitHandler);
		document.addEventListener('webkitfullscreenchange', exitHandler);
		document.addEventListener('mozfullscreenchange', exitHandler);
		document.addEventListener('MSFullscreenChange', exitHandler);
	}

	/**
	 * addCustomTimeRange - Git#52
	 * @param id id 
	 * @param start start 
	 * @param end end 
	 */
	addCustomTimeRange(id: string, start: Date, end: Date) {
		const existingRange = this.customTimeRanges.find((x) => x.panelID == id);
		if (existingRange) {
			existingRange.startDate = start;
			existingRange.endDate = end;
		} else {
			this.customTimeRanges.push({
				panelID: id,
				startDate: start,
				endDate: end
			});
		}

		// this.resetData();
		// this.loadPanels();
		this.publicPanelData[parseInt(id)] = undefined;
		this.loadSpecificPanel(id);
	}

	/**
	 * removeCustomTimeRange - Git#52
	 * @param id id 
	 */
	removeCustomTimeRange(id: string) {
		this.customTimeRanges = this.customTimeRanges.filter((x) => x.panelID != id);
		this.publicPanelData[parseInt(id)] = undefined;
		this.loadSpecificPanel(id);
	}

	/**
	 * handleCustomRangeChange - Git#52
	 * @param panelData panelData 
	 * @param event event 
	 */
	handleCustomRangeChange(panelData: any, event: any) {
		if (event) {
			this.addCustomTimeRange(panelData.id, event.start, event.end);
		} else {
			this.removeCustomTimeRange(panelData.id);
		}
	}

	/**
	 * loadSpecificPanel - Git#52
	 * @param id id 
	 */
	async loadSpecificPanel(id: string) {
		const panelInfo = this.panelInfo.find((x) => x.id == id);

		if (panelInfo) {
			const context = { ...panelInfo.context, id: parseInt(panelInfo.id), isHierarchy: panelInfo.isHierarchy, barDefault: panelInfo.barDefault };

			if (panelInfo.template == "Chart") {
				this.chartBars.push({ panelID: parseInt(panelInfo.id), barSeries: panelInfo.barDefault ?? false });
			}

			this.renderTemplateIntoPanel(
				panelInfo.id,
				panelInfo.isHierarchy ? this.hierarchyTemplates[panelInfo.templateID] : this.templates[panelInfo.templateID],
				context
			);

			await this.getPanelData(panelInfo, panelInfo.isHierarchy);
		}
	}
}
